<div class="modal-header">
  <h4 class="modal-title">
    <i class="bi bi-tag-fill me-2 text-danger"></i>
    {{ isEditing ? 'Edit Offer' : 'Add Offer' }}
  </h4>
  <!-- X close only makes sense in modal mode. In page mode (the
       /create-offer route) the user has back/breadcrumb nav and the
       Cancel button in the footer. -->
  @if (isModal) {
    <button type="button" class="btn-close" aria-label="Close" (click)="cancel()"></button>
  }
</div>

<div class="modal-body">
  <!-- ═══ METADATA STRIP (edit mode only) ════════════════════════════
       Audit + at-a-glance facts that USED to live in the list columns
       (Base Price, Created At). Removed from the list to keep it scannable,
       surfaced here so admins still have one click to "who set this up
       and what does it actually do".
       Renders nothing on the Add flow (no audit data yet, no scope yet). -->
  @if (isEditing) {
    <div class="offer-metadata">
      <div class="meta-cell">
        <div class="meta-label">Current Status</div>
        <div class="meta-value">
          @switch (derivedStatus) {
            @case ('ACTIVE')    { <span class="badge bg-success"><i class="bi bi-check-circle-fill me-1"></i> Active</span> }
            @case ('PAUSED')    { <span class="badge bg-secondary"><i class="bi bi-pause-circle-fill me-1"></i> Paused</span> }
            @case ('SCHEDULED') { <span class="badge bg-info text-dark"><i class="bi bi-calendar-event-fill me-1"></i> Scheduled</span> }
            @case ('EXPIRED')   { <span class="badge bg-danger"><i class="bi bi-clock-history me-1"></i> Expired</span> }
          }
        </div>
      </div>

      @if (rewardSummary) {
        <div class="meta-cell">
          <div class="meta-label">Reward</div>
          <div class="meta-value fw-semibold">{{ rewardSummary }}</div>
        </div>
      }

      <div class="meta-cell">
        <div class="meta-label">Scope</div>
        <div class="meta-value">
          <i class="bi bi-shop me-1 text-muted"></i>{{ scopeSummary }}
        </div>
      </div>

      <div class="meta-cell">
        <div class="meta-label">Created</div>
        <div class="meta-value">
          @if (data?.createdAt) {
            {{ data.createdAt | date : 'dd-MM-yyyy HH:mm' }}
          } @else { — }
          @if (createdByLabel) {
            <div class="meta-sub">by {{ createdByLabel }}</div>
          }
        </div>
      </div>

      <div class="meta-cell">
        <div class="meta-label">Last Updated</div>
        <div class="meta-value">
          @if (data?.updatedAt) {
            {{ data.updatedAt | date : 'dd-MM-yyyy HH:mm' }}
          } @else { — }
          @if (updatedByLabel) {
            <div class="meta-sub">by {{ updatedByLabel }}</div>
          }
        </div>
      </div>
    </div>
  }

  <form [formGroup]="offerForm" class="offer-form">

    <!-- ═══ SECTION 1 · Basics ═════════════════════════════════════════
         Every offer regardless of type needs these. Kept at the top so
         admins fill the cheap fields before the type-specific stuff
         (which can be re-rendered if they change the type later). -->
    <section class="offer-section">
      <header class="offer-section-header">
        <h5><i class="bi bi-1-circle-fill me-1 text-danger"></i> Basics</h5>
        <small class="text-muted">Name, type, status, and active window</small>
      </header>

      <!-- Status kill-switch. Sits at the top of BASICS because it's the
           most consequential field on the form — flipping this off pulls
           the offer from the POS instantly regardless of date range. The
           inline badge mirrors the toggle so the admin sees the current
           state without parsing checkbox semantics. -->
      <div class="offer-status-row" [class.is-active]="offerForm.get('isActive')?.value"
                                    [class.is-inactive]="!offerForm.get('isActive')?.value">
        <div class="d-flex align-items-center">
          <div class="form-check form-switch mb-0">
            <input class="form-check-input" type="checkbox" role="switch"
                   id="isActiveToggle" formControlName="isActive">
            <label class="form-check-label fw-semibold" for="isActiveToggle">
              @if (offerForm.get('isActive')?.value) {
                <span class="badge bg-success me-2">
                  <i class="bi bi-check-circle-fill me-1"></i> ACTIVE
                </span>
                This offer will apply at the POS when the date range is current.
              } @else {
                <span class="badge bg-secondary me-2">
                  <i class="bi bi-pause-circle-fill me-1"></i> INACTIVE
                </span>
                Offer is paused — will NOT apply at the POS even if today is in the date range.
              }
            </label>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label">
            Offer Type <span class="text-danger">*</span>
          </label>
          <select class="form-select" formControlName="offerType">
            @for (offer of offerTypes; track offer.value) {
              <option [value]="offer.value">{{ offer.label }}</option>
            }
          </select>
          @if (typeHint) {
            <div class="form-text small">{{ typeHint }}</div>
          }
        </div>

        <div class="col-md-4">
          <label class="form-label">
            Offer Name <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control" formControlName="offerName"
                 placeholder="e.g. SUMMER10, WEEKEND100" />
        </div>

        <div class="col-md-4">
          <label class="form-label">Description</label>
          <input type="text" class="form-control" formControlName="description"
                 placeholder="Internal note — not shown to customer" />
        </div>

        <div class="col-md-3">
          <label class="form-label">
            Start Date <span class="text-danger">*</span>
          </label>
          <input type="date" class="form-control" formControlName="startDate" />
        </div>

        <div class="col-md-3">
          <label class="form-label">
            End Date <span class="text-danger">*</span>
          </label>
          <input type="date" class="form-control" formControlName="endDate" />
        </div>

        @if (isFieldVisible('startTime')) {
          <div class="col-md-3">
            <label class="form-label">Daily Start Time @if (isRequired('startTime')) { <span class="text-danger">*</span> }</label>
            <input type="time" class="form-control" formControlName="startTime" />
          </div>
        }

        @if (isFieldVisible('endTime')) {
          <div class="col-md-3">
            <label class="form-label">Daily End Time @if (isRequired('endTime')) { <span class="text-danger">*</span> }</label>
            <input type="time" class="form-control" formControlName="endTime" />
            <div class="form-text small">Store clock, 24-hour. An end before the start wraps past midnight.</div>
          </div>
        }
      </div>
    </section>

    <!-- ═══ SECTION 2 · Rule (qualification + reward) ══════════════════
         The dynamic part. What the customer needs to do (bill range,
         buy qty) and what they get (percent off, free item, etc.).
         Renders only the fields the chosen offer type actually uses. -->
    <section class="offer-section">
      <header class="offer-section-header">
        <h5><i class="bi bi-2-circle-fill me-1 text-danger"></i> Offer Rule</h5>
        <small class="text-muted">What qualifies the customer and what they get</small>
      </header>

      <!-- Bill-offer resolution-rule explainer. Shown for all 4 bill
           types so admins know "only one applies per cart" is by design. -->
      @if (isFieldVisible('billAmountMin')) {
        <div class="alert alert-info py-2 small mb-3 d-flex">
          <i class="bi bi-info-circle-fill me-2 mt-1"></i>
          <div>
            <strong>Multiple bill offers can be active at the same time.</strong>
            If more than one qualifies for a given cart, the system picks <strong>one</strong>
            using: <em>priority (lower number wins)</em> &rarr; <em>highest rupee value</em> &rarr;
            stable tie-break. Customer always gets the best deal automatically.
          </div>
        </div>
      }

      <div class="row g-3">
        <!-- Bill amount range -->
        @if (isFieldVisible('billAmountMin')) {
          <div class="col-md-3">
            <label class="form-label">
              Min Bill Amount @if (isRequired('billAmountMin')) { <span class="text-danger">*</span> }
            </label>
            <div class="input-group">
              <span class="input-group-text">₹</span>
              <input type="number" min="0" class="form-control" formControlName="billAmountMin" />
            </div>
          </div>
        }

        @if (isFieldVisible('billAmountMax')) {
          <div class="col-md-3">
            <label class="form-label">
              Max Bill Amount
              <small class="text-muted">(0 = no cap)</small>
            </label>
            <div class="input-group">
              <span class="input-group-text">₹</span>
              <input type="number" min="0" class="form-control" formControlName="billAmountMax" />
            </div>
          </div>
        }

        <!-- Reward: amount / percent -->
        @if (isFieldVisible('priceOffAmount')) {
          <div class="col-md-3">
            <label class="form-label">
              {{ amountLabel }} @if (isRequired('priceOffAmount')) { <span class="text-danger">*</span> }
            </label>
            <div class="input-group">
              <span class="input-group-text">₹</span>
              <input type="number" min="0" class="form-control" formControlName="priceOffAmount" />
            </div>
            @if (offerForm.get('offerType')?.value === 'BuyXGetYFree') {
              <div class="form-text small">Leave blank (and no percent) for Y units free.</div>
            }
          </div>
        }

        @if (isFieldVisible('percentOff')) {
          <div class="col-md-3">
            <label class="form-label">
              {{ percentLabel }} @if (isRequired('percentOff')) { <span class="text-danger">*</span> }
            </label>
            <div class="input-group">
              <input type="number" min="0" max="100" class="form-control" formControlName="percentOff" />
              <span class="input-group-text">%</span>
            </div>
          </div>
        }

        <!-- Buy / Free quantities for BuyXGetYFree-style offers -->
        @if (isFieldVisible('buyQuantity')) {
          <div class="col-md-3">
            <label class="form-label">
              Buy Quantity @if (isRequired('buyQuantity')) { <span class="text-danger">*</span> }
            </label>
            <input type="number" min="0" class="form-control" formControlName="buyQuantity" />
            @if (offerForm.get('offerType')?.value === 'BuyXYGetZSpecialPrice') {
              <div class="form-text small">Optional: "any N of the picked products" for the special price. Blank = one of each.</div>
            }
          </div>
        }

        @if (isFieldVisible('freeQuantity')) {
          <div class="col-md-3">
            <label class="form-label">
              Free Quantity @if (isRequired('freeQuantity')) { <span class="text-danger">*</span> }
            </label>
            <input type="number" min="0" class="form-control" formControlName="freeQuantity" />
          </div>
        }

        <!-- Progressive discount slabs: buy more, save more -->
        @if (isFieldVisible('progressiveDiscount')) {
          <div class="col-md-12">
            <label class="form-label fw-bold">
              Quantity Slabs <span class="text-danger">*</span>
            </label>
            <div class="form-text small mb-2">
              The highest slab the line quantity reaches applies. Give a percent OR an amount per unit for each slab.
            </div>
            <div formArrayName="slabs">
              @for (row of slabsArray.controls; track $index; let i = $index) {
                <div class="row g-2 align-items-end mb-2" [formGroupName]="i">
                  <div class="col-md-3">
                    <label class="form-label small text-muted mb-1">Minimum quantity</label>
                    <input type="number" min="1" class="form-control form-control-sm" formControlName="minQty" />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small text-muted mb-1">Percent off</label>
                    <div class="input-group input-group-sm">
                      <input type="number" min="0" max="100" class="form-control" formControlName="percentOff" />
                      <span class="input-group-text">%</span>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small text-muted mb-1">or Amount off per unit</label>
                    <div class="input-group input-group-sm">
                      <span class="input-group-text">₹</span>
                      <input type="number" min="0" class="form-control" formControlName="priceOffAmount" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeSlabRow(i)" title="Remove slab">
                      <i class="bi bi-trash3"></i>
                    </button>
                  </div>
                </div>
              }
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary" (click)="addSlabRow()">
              <i class="bi bi-plus-lg"></i> Add slab
            </button>
          </div>
        }

        <!-- Legacy single free-item picker (BuyXYGetZFree etc.) -->
        @if (isFieldVisible('freeItemVariantId')) {
          <div class="col-md-12">
            <label class="form-label">
              Free Item <span class="text-danger">*</span>
            </label>
            <ng-select
              [items]="variantList"
              bindLabel="displayLabel"
              bindValue="id"
              [typeahead]="productInput$"
              [loading]="loading"
              formControlName="freeItemVariantId"
              placeholder="Type product name, SKU, or barcode to search"
              (open)="onProductSelectFocus()">
              <ng-template ng-label-tmp let-item="item">
                {{ item.longDescription || item.variantName }}
                @if (item.productSku) { <small class="text-muted">· {{ item.productSku }}</small> }
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                <div class="fw-semibold">{{ item.longDescription || item.variantName }}</div>
                <small class="text-muted">
                  @if (item.variantName && item.variantName !== item.longDescription) { {{ item.variantName }} }
                  @if (item.productSku) { · SKU {{ item.productSku }} }
                  @if (item.barcode) { · {{ item.barcode }} }
                </small>
                <div class="d-flex gap-2 mt-1 small">
                  <span class="badge bg-light text-dark border">CP: <strong>{{ item.costPrice != null ? ('₹' + (item.costPrice | number:'1.2-2')) : '—' }}</strong></span>
                  <span class="badge bg-light text-dark border">SP: <strong>{{ item.sellingPrice != null ? ('₹' + (item.sellingPrice | number:'1.2-2')) : '—' }}</strong></span>
                  <span class="badge bg-light text-dark border">MRP: <strong>{{ item.mrp != null ? ('₹' + (item.mrp | number:'1.2-2')) : '—' }}</strong></span>
                </div>
              </ng-template>
            </ng-select>
          </div>
        }

        <!-- CHOICE_N control: only for BillRangeGetFreeItem. Default 1
             keeps legacy single-pick UX. Set higher to enable "pick any
             N of M" multi-select at the POS. -->
        @if (isFieldVisible('freeChoiceCount')) {
          <div class="col-md-4">
            <label class="form-label">
              Number of Free Picks <span class="text-danger">*</span>
            </label>
            <input type="number" min="1" class="form-control" formControlName="freeChoiceCount" />
            <div class="form-text small">
              How many items the cashier picks from the list below.
              <strong>1</strong> = customer chooses one gift (default).
              <strong>2+</strong> = "pick any N of M" multi-select picker.
            </div>
          </div>
        }

        <!-- Multi-variant free items: CHOICE (X OR Y) or SET (X AND Y) -->
        @if (isFieldVisible('freeItems')) {
          <div class="col-md-12">
            <div class="free-items-block">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <label class="form-label mb-0 fw-bold">
                    {{ offerForm.get('offerType')?.value === 'BuyXYGetZSpecialPrice' ? 'Items at the special price' : 'Free Items' }}
                    @if (isRequired('freeItems')) { <span class="text-danger">*</span> }
                  </label>
                  <div class="small text-muted">
                    @if (offerForm.get('offerType')?.value === 'BillRangeGetFreeSet') {
                      <i class="bi bi-collection-fill me-1"></i>
                      Customer gets <strong>all</strong> of these together (SET — add 2 or more)
                    } @else if (offerForm.get('offerType')?.value === 'BuyXYGetZFree') {
                      <i class="bi bi-gift-fill me-1"></i>
                      Added <strong>free</strong> (×Qty per set) when every bundle product is on the bill
                    } @else if (offerForm.get('offerType')?.value === 'BuyXYGetZSpecialPrice') {
                      <i class="bi bi-tag-fill me-1"></i>
                      Optional: these sell at the <strong>special price</strong> when the bundle is bought (they must be scanned). Leave empty to price the bundle itself.
                    } @else if ((offerForm.get('freeChoiceCount')?.value || 1) > 1) {
                      <i class="bi bi-ui-checks me-1"></i>
                      Cashier picks <strong>{{ offerForm.get('freeChoiceCount')?.value }}</strong>
                      of these at POS (CHOICE_N — add at least {{ offerForm.get('freeChoiceCount')?.value }} variants)
                    } @else {
                      <i class="bi bi-arrow-left-right me-1"></i>
                      Cashier picks <strong>one</strong> of these at POS (OR — add 2 or more for choice)
                    }
                  </div>
                </div>
                @if (freeItemsArray.controls.length > 0) {
                  <button type="button" class="btn btn-sm btn-primary" (click)="addFreeItemRow()">
                    <i class="bi bi-plus-lg"></i> Add Variant
                  </button>
                }
              </div>

              <div formArrayName="freeItems">
                @for (row of freeItemsArray.controls; track $index; let i = $index) {
                  <div class="free-item-row" [formGroupName]="i">
                    <div class="free-item-row-index">{{ i + 1 }}</div>
                    <div class="flex-grow-1">
                      <ng-select
                        [items]="variantList"
                        bindLabel="displayLabel"
                        bindValue="id"
                        [typeahead]="productInput$"
                        [loading]="loading"
                        formControlName="variantId"
                        placeholder="Type product name, SKU, or barcode to search"
                        (open)="onProductSelectFocus()">
                        <ng-template ng-label-tmp let-item="item">
                          {{ item.longDescription || item.variantName }}
                          @if (item.productSku) { <small class="text-muted">· {{ item.productSku }}</small> }
                        </ng-template>
                        <ng-template ng-option-tmp let-item="item">
                          <div class="fw-semibold">{{ item.longDescription || item.variantName }}</div>
                          <small class="text-muted">
                            @if (item.variantName && item.variantName !== item.longDescription) { {{ item.variantName }} }
                            @if (item.productSku) { · SKU {{ item.productSku }} }
                            @if (item.barcode) { · {{ item.barcode }} }
                          </small>
                          <div class="d-flex gap-2 mt-1 small">
                            <span class="badge bg-light text-dark border">CP: <strong>{{ item.costPrice != null ? ('₹' + (item.costPrice | number:'1.2-2')) : '—' }}</strong></span>
                            <span class="badge bg-light text-dark border">SP: <strong>{{ item.sellingPrice != null ? ('₹' + (item.sellingPrice | number:'1.2-2')) : '—' }}</strong></span>
                            <span class="badge bg-light text-dark border">MRP: <strong>{{ item.mrp != null ? ('₹' + (item.mrp | number:'1.2-2')) : '—' }}</strong></span>
                          </div>
                        </ng-template>
                      </ng-select>
                    </div>
                    <div class="free-item-row-qty">
                      <label class="form-label small text-muted mb-1">Qty</label>
                      <input type="number" min="1" class="form-control form-control-sm" formControlName="quantity" />
                    </div>
                    <div class="free-item-row-actions">
                      <button type="button" class="btn btn-sm btn-outline-danger"
                              (click)="removeFreeItemRow(i)" title="Remove this variant">
                        <i class="bi bi-trash3"></i>
                      </button>
                    </div>
                  </div>
                }

                <!-- Prominent empty-state CTA. Big dashed box that's
                     unmissable; clicking anywhere on it adds the first
                     row. Replaces the old tiny text-link admin UX. -->
                @if (freeItemsArray.controls.length === 0) {
                  <button type="button" class="free-items-empty" (click)="addFreeItemRow()">
                    <i class="bi bi-plus-circle-dotted display-6"></i>
                    <div class="fw-semibold mt-2">Add your first free variant</div>
                    <small class="text-muted">
                      Click here to pick a product. Each row shows CP / SP / MRP so you can compare.
                    </small>
                  </button>
                }

                <!-- Soft validation hint for SET offers with &lt; 2 rows -->
                @if (offerForm.get('offerType')?.value === 'BillRangeGetFreeSet'
                     && freeItemsArray.controls.length === 1) {
                  <div class="alert alert-warning py-2 small mt-2 mb-0">
                    <i class="bi bi-exclamation-triangle-fill me-1"></i>
                    A SET offer usually has 2 or more items (X AND Y). Add one more variant,
                    or switch to <strong>Bill Range — Free Item</strong> for a single freebie.
                  </div>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </section>

    <!-- ═══ SECTION 3 · Scope (where + what it applies to) ═════════════
         Variants / categories / departments / brands / exclusions /
         stores. All optional except when the offer type semantics
         require a target (e.g. BrandPercentDiscount needs a brand). -->
    <section class="offer-section">
      <header class="offer-section-header">
        <h5><i class="bi bi-3-circle-fill me-1 text-danger"></i> Scope</h5>
        <small class="text-muted">Where this offer applies — stores, products, categories</small>
      </header>

      <div class="row g-3">
        <!-- Stores: universally available (empty = all stores) -->
        @if (isFieldVisible('storeIds')) {
          <div class="col-md-12">
            <label class="form-label">
              Applicable Stores
              <small class="text-muted">Leave empty to apply at ALL stores</small>
            </label>
            <ng-select
              [items]="stores"
              bindLabel="name"
              bindValue="id"
              [multiple]="true"
              formControlName="storeIds"
              placeholder="Select stores, or leave empty for all">
            </ng-select>
          </div>
        }

        <!-- Variant scope -->
        @if (isFieldVisible('variantIds')) {
          <div class="col-md-12">
            <label class="form-label">
              Applicable Variants @if (isRequired('variantIds')) { <span class="text-danger">*</span> } @else { <small class="text-muted">(optional — blank = every product)</small> }
            </label>
            <ng-select
              [items]="variantList"
              bindLabel="displayLabel"
              bindValue="id"
              [multiple]="true"
              [typeahead]="productInput$"
              [loading]="loading"
              [hideSelected]="true"
              formControlName="variantIds"
              placeholder="Type product name, SKU, or barcode to search"
              (open)="onProductSelectFocus()">
              <ng-template ng-label-tmp let-item="item">
                {{ item.longDescription || item.variantName }}
                @if (item.productSku) { <small class="text-muted">· {{ item.productSku }}</small> }
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                <div class="fw-semibold">{{ item.longDescription || item.variantName }}</div>
                <small class="text-muted">
                  @if (item.variantName && item.variantName !== item.longDescription) { {{ item.variantName }} }
                  @if (item.productSku) { · SKU {{ item.productSku }} }
                  @if (item.barcode) { · {{ item.barcode }} }
                </small>
                <div class="d-flex gap-2 mt-1 small">
                  <span class="badge bg-light text-dark border">CP: <strong>{{ item.costPrice != null ? ('₹' + (item.costPrice | number:'1.2-2')) : '—' }}</strong></span>
                  <span class="badge bg-light text-dark border">SP: <strong>{{ item.sellingPrice != null ? ('₹' + (item.sellingPrice | number:'1.2-2')) : '—' }}</strong></span>
                  <span class="badge bg-light text-dark border">MRP: <strong>{{ item.mrp != null ? ('₹' + (item.mrp | number:'1.2-2')) : '—' }}</strong></span>
                </div>
              </ng-template>
            </ng-select>
          </div>
        }

        <!-- Per-SKU offer prices (MRP-off-list upload). Shown read-only so
             the admin can see what each SKU sells at; the Discount Amount
             field is not used for these lines. -->
        @if (priceLines.length) {
          <div class="col-md-12">
            <div class="alert alert-light border small mb-2">
              <i class="bi bi-table me-1"></i>
              This offer came from a <strong>sheet upload</strong>: every SKU below carries its own value
              (for the listed MRP, or every MRP when none is listed), so the Discount Amount / Percent field is ignored for them.
              Removing a product from the picker removes its line. To change prices, delete this offer and upload
              a corrected sheet.
            </div>
            <div class="table-responsive" style="max-height: 280px; overflow-y: auto;">
              <table class="table table-sm table-bordered mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 110px;">SKU</th>
                    <th>Product</th>
                    <th class="text-end" style="width: 110px;">MRP</th>
                    <th class="text-end" style="width: 120px;">Offer Price</th>
                    <th class="text-end" style="width: 110px;">% Off MRP</th>
                    <th class="text-end" style="width: 110px;">Amount Off</th>
                  </tr>
                </thead>
                <tbody>
                  @for (l of priceLines; track $index) {
                    <tr>
                      <td>{{ l.sku }}</td>
                      <td>{{ l.name }}</td>
                      <td class="text-end">{{ l.mrp != null ? ('₹' + (l.mrp | number:'1.2-2')) : '—' }}</td>
                      <td class="text-end fw-semibold">{{ l.offerPrice != null ? ('₹' + (l.offerPrice | number:'1.2-2')) : '—' }}</td>
                      <td class="text-end fw-semibold">{{ l.percentOff != null ? ((l.percentOff | number:'1.0-2') + '%') : '—' }}</td>
                      <td class="text-end fw-semibold">{{ l.priceOffAmount != null ? ('₹' + (l.priceOffAmount | number:'1.2-2')) : '—' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <div class="form-text">{{ priceLines.length }} priced SKU(s).</div>
          </div>
        }

        @if (isFieldVisible('exclusionVariantIds')) {
          <div class="col-md-12">
            <label class="form-label">Exclude Variants</label>
            <ng-select
              [items]="variantList"
              bindLabel="displayLabel"
              bindValue="id"
              [multiple]="true"
              [typeahead]="productInput$"
              [loading]="loading"
              [hideSelected]="true"
              formControlName="exclusionVariantIds"
              placeholder="Type product name, SKU, or barcode to exclude"
              (open)="onProductSelectFocus()">
              <ng-template ng-label-tmp let-item="item">
                {{ item.longDescription || item.variantName }}
                @if (item.productSku) { <small class="text-muted">· {{ item.productSku }}</small> }
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                <div class="fw-semibold">{{ item.longDescription || item.variantName }}</div>
                <small class="text-muted">
                  @if (item.variantName && item.variantName !== item.longDescription) { {{ item.variantName }} }
                  @if (item.productSku) { · SKU {{ item.productSku }} }
                  @if (item.barcode) { · {{ item.barcode }} }
                </small>
                <div class="d-flex gap-2 mt-1 small">
                  <span class="badge bg-light text-dark border">CP: <strong>{{ item.costPrice != null ? ('₹' + (item.costPrice | number:'1.2-2')) : '—' }}</strong></span>
                  <span class="badge bg-light text-dark border">SP: <strong>{{ item.sellingPrice != null ? ('₹' + (item.sellingPrice | number:'1.2-2')) : '—' }}</strong></span>
                  <span class="badge bg-light text-dark border">MRP: <strong>{{ item.mrp != null ? ('₹' + (item.mrp | number:'1.2-2')) : '—' }}</strong></span>
                </div>
              </ng-template>
            </ng-select>
          </div>
        }

        <!-- Legacy free-form scope inputs — TODO: convert to proper pickers -->
        @if (isFieldVisible('categoryIds')) {
          <div class="col-md-6">
            <label class="form-label">Applicable Categories</label>
            <input type="text" class="form-control" formControlName="categoryIds"
                   placeholder="Comma-separated category IDs" />
          </div>
        }

        @if (isFieldVisible('departmentIds')) {
          <div class="col-md-6">
            <label class="form-label">Applicable Departments</label>
            <input type="text" class="form-control" formControlName="departmentIds"
                   placeholder="Comma-separated department IDs" />
          </div>
        }

        @if (isFieldVisible('brandIds')) {
          <div class="col-md-6">
            <label class="form-label">Applicable Brands</label>
            <input type="text" class="form-control" formControlName="brandIds"
                   placeholder="Comma-separated brand IDs" />
          </div>
        }
      </div>
    </section>

    <!-- ═══ SECTION 4 · Conflict resolution ═════════════════════════════
         Priority + Combinable. Grouped because both affect what happens
         when multiple offers qualify on the same bill. -->
    <section class="offer-section">
      <header class="offer-section-header">
        <h5><i class="bi bi-4-circle-fill me-1 text-danger"></i> Conflict Resolution</h5>
        <small class="text-muted">Used only when more than one offer qualifies for the same cart</small>
      </header>

      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label">
            Priority
            <small class="text-muted">(lower number wins)</small>
          </label>
          <input type="number" min="0" class="form-control" formControlName="priority" />
          <div class="form-text small">
            Default 0. Set lower if you want this offer to beat others in a tie.
          </div>
        </div>

        <div class="col-md-8 d-flex align-items-center">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="combinable" formControlName="combinable" />
            <label class="form-check-label" for="combinable">
              <strong>Combinable with other offers</strong>
              <div class="small text-muted">
                Each line gets one price offer (store-pinned first, then priority, then the bigger discount).
                Tick this to let a bundle / buy-X-get-Y offer stack on top of it on the same line; untick to
                protect the line unless the bundle offer has a better (lower) priority.
              </div>
            </label>
          </div>
        </div>
      </div>
    </section>

  </form>
</div>

<div class="modal-footer">
  <button type="button" class="btn btn-outline-secondary" (click)="cancel()" [disabled]="saving">
    Cancel
  </button>
  <button type="button" class="btn btnprimary" (click)="saveOffer()" [disabled]="saving">
    @if (saving) {
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      {{ isEditing ? 'Updating…' : 'Saving…' }}
    } @else {
      <i class="bi bi-check2 me-1"></i>
      {{ isEditing ? 'Update Offer' : 'Save Offer' }}
    }
  </button>
</div>
